import { Component, Input, OnDestroy, OnInit } from '@angular/core';
import { ActivityEditorBaseComponent } from '../../activity-editor-base/activity-editor-base.component';
import {
  SecurityService,
  WorkflowContextService,
  WorkflowService
} from 'src/app/services';
import {
  AbstractControl,
  UntypedFormBuilder,
  UntypedFormControl,
  UntypedFormGroup,
  ValidationErrors,
  Validators
} from '@angular/forms';
import {
  ConsolidatedInspectionActivity,
  ConsolidatedInspectionActivityModel,
  ConsolidatedInspectionSections,
  DataEntityLayoutModel,
  FormActivity,
  FormActivityModel,
  ScheduledCalendarEventConfig,
  SingleColumnFormLayoutModel
} from 'src/app/models/activities';
import { Role } from 'src/app/models/role';
import { Actions } from 'src/app/models';
import { Subscription } from 'rxjs';
import { DataEntityItemToolButton } from '../../form-activity-editor/data-entity-item/data-entity-item.component';

@Component({
  selector: 'wm-consolidated-inspection-activity-editor',
  templateUrl: './consolidated-inspection-activity-editor.component.html',
  styleUrl: './consolidated-inspection-activity-editor.component.css',
  standalone: false
})
export class ConsolidatedInspectionActivityEditorComponent
  extends ActivityEditorBaseComponent
  implements OnInit {
  @Input() activity: ConsolidatedInspectionActivity;

  availableScheduleRoles: Role[];
  availableCompleteRoles: Role[];

  requestStepActivity: FormActivity;
  scheduleStepActivity: FormActivity;
  completeStepActivity: FormActivity;

  requestForm: UntypedFormGroup;
  scheduleForm: UntypedFormGroup;
  completeForm: UntypedFormGroup;
  onRequestedEmailForm: UntypedFormGroup;
  onScheduledEmailForm: UntypedFormGroup;
  onScheduledCalendarForm: UntypedFormGroup;

  canEditCompleteStep = false;

  /**
   * True when the current login has the DO_ANYTHING (System Administrator)
   * action. Only sys admins may flag Complete Step column entities as "No
   * Schedule Only".
   */
  isSysAdmin = false;

  /**
   * Set of Complete Step column entity object references that are flagged as
   * "No Schedule Only". We track by reference during editing so that drag /
   * add / delete operations in the shared form-activity-editor stay in sync
   * without needing to update the shared DataEntityLayoutModel classes. On
   * save we translate this set into positional indices in the parent
   * activity's merged `formLayoutModel.columnEntities` list, which is what
   * gets persisted to `noScheduleOnlyColumnEntityIndices`.
   */
  noScheduleFlagged: Set<DataEntityLayoutModel> = new Set<
    DataEntityLayoutModel
  >();

  /**
   * Tool-panel button descriptor(s) threaded into the Complete Step
   * `<wm-form-activity-editor>` so each column entity renders its own
   * "No Schedule Only" toggle icon. Empty when the current login is not a
   * System Administrator (DO_ANYTHING).
   */
  private _noScheduleOnlyToolButtons: DataEntityItemToolButton[] = [
    {
      icon: 'event_busy',
      // Hide entirely when the icon isn't available for this column entity —
      // either the current login lacks DO_ANYTHING or the entity contains a
      // system data entity (which can never be flagged as No Schedule Only).
      hidden: (de: DataEntityLayoutModel) =>
        !this.isSysAdmin || this.columnEntityContainsSystemEntity(de),
      isActive: (de: DataEntityLayoutModel) => this.isNoScheduleOnly(de),
      tooltip: (de: DataEntityLayoutModel) =>
        this.isNoScheduleOnly(de)
          ? 'No Schedule Only — click to show in all inspections'
          : 'Set as No Schedule Only (hidden — only shows for No Schedule inspections)',
      onClick: (de: DataEntityLayoutModel) => this.toggleNoScheduleOnly(de)
    }
  ];

  get noScheduleOnlyToolButtons(): DataEntityItemToolButton[] {
    return this.isSysAdmin ? this._noScheduleOnlyToolButtons : [];
  }

  constructor(
    private _context: WorkflowContextService,
    private _securitySvc: SecurityService,
    public workflowSvc: WorkflowService
  ) {
    super();
  }

  ngOnInit() {
    this.activity.model = this.activity
      .model as ConsolidatedInspectionActivityModel;

    this._securitySvc
      .isLoginEntitled(
        Actions.ADD_CONSOLIDATED_INSPECTION_ACTIVITY,
        null,
        null,
        !this._context.client
      )
      .subscribe(isEntitled => {
        this.canEditCompleteStep = isEntitled;

        this.init();
      });

    // Sys admin (DO_ANYTHING) gate for the "No Schedule Only" column entity
    // flagging UI. Independent of the complete-step edit entitlement above.
    this._securitySvc
      .isLoginEntitled(Actions.DO_ANYTHING)
      .subscribe(isEntitled => {
        this.isSysAdmin = isEntitled;
      });
  }

  init() {
    this.getAvailableRoles();

    // this is done to leverage the constructor's newing up and casting of its model
    this.requestStepActivity = new FormActivity(
      (this.activity
        .model as ConsolidatedInspectionActivityModel).requestStepActivity
    );
    this.scheduleStepActivity = new FormActivity(
      (this.activity
        .model as ConsolidatedInspectionActivityModel).scheduleStepActivity
    );
    this.completeStepActivity = new FormActivity(
      (this.activity
        .model as ConsolidatedInspectionActivityModel).completeStepActivity
    );

    this.requestForm = new UntypedFormGroup({});
    this.scheduleForm = new UntypedFormGroup({});
    this.completeForm = new UntypedFormGroup({});
    this.onRequestedEmailForm = new UntypedFormGroup({});
    this.onScheduledEmailForm = new UntypedFormGroup({});
    this.onScheduledCalendarForm = new UntypedFormGroup({});

    this.initOnScheduledCalendarForm();

    this.hydrateNoScheduleFlagged();

    // Each child form editor normalizes its layout model during ngOnInit, which
    // replaces the layout entry objects hydrated above. Rebind the persisted
    // indices to those final objects after Angular initializes the child views.
    setTimeout(() => this.hydrateNoScheduleFlagged());
  }

  /**
   * Translate the persisted `noScheduleOnlyColumnEntityIndices` (positions in
   * the parent activity's merged `formLayoutModel.columnEntities` list) back
   * into references to the corresponding Complete Step layout models on
   * `completeStepActivity`. We walk the merged list in the order it was
   * saved (request-section entries, then schedule-section entries, then
   * complete-section entries) and match each flagged merged-index to its
   * complete-step layout model by section-local position.
   */
  private hydrateNoScheduleFlagged() {
    this.noScheduleFlagged = new Set<DataEntityLayoutModel>();

    const model = this.activity.model as ConsolidatedInspectionActivityModel;

    const indices = model?.noScheduleOnlyColumnEntityIndices;

    if (!Array.isArray(indices) || indices.length === 0) {
      return;
    }

    const merged =
      (this.activity.model.formLayoutModel as SingleColumnFormLayoutModel)
        ?.columnEntities ?? [];

    const completeStepEntries =
      (this.completeStepActivity?.model
        ?.formLayoutModel as SingleColumnFormLayoutModel)?.columnEntities ?? [];

    if (merged.length === 0 || completeStepEntries.length === 0) {
      return;
    }

    // Walk the merged list; each complete-section entry we encounter maps
    // by order to a completeStepActivity layout model. This is robust
    // regardless of how many request/schedule entries precede it, and it
    // doesn't rely on object identity across the FormActivity reconstitution
    // that happens in init().
    const indexSet = new Set<number>(indices);

    let completeLocalIdx = 0;

    for (let i = 0; i < merged.length; i++) {
      const ce = merged[i];

      if (
        ce?.sectionId !== ConsolidatedInspectionSections.COMPLETE_SECTION_ID
      ) {
        continue;
      }

      if (indexSet.has(i)) {
        const targetRef = completeStepEntries[completeLocalIdx];

        if (targetRef) {
          this.noScheduleFlagged.add(targetRef);
        }
      }

      completeLocalIdx++;
    }
  }

  /**
   * Sets up the sub-form used to configure the .ics calendar attachment on the
   * OnScheduledEmail. Mirrors AppointmentActivityEditorComponent's form controls
   * for location / allDay / duration_minutes.
   */
  initOnScheduledCalendarForm() {
    const model = this.activity.model as ConsolidatedInspectionActivityModel;

    if (!model.onScheduledCalendarEvent) {
      model.onScheduledCalendarEvent = new ScheduledCalendarEventConfig();
    }

    this.onScheduledCalendarForm.addControl(
      'sched_appt_location',
      new UntypedFormControl('', Validators.required)
    );

    this.onScheduledCalendarForm.addControl(
      'sched_appt_duration',
      new UntypedFormControl(
        '',
        (control: AbstractControl): ValidationErrors | null => {
          const m = this.activity.model as ConsolidatedInspectionActivityModel;

          // Only required when the toggle is on and the event is not all-day.
          if (
            m.sendOnScheduledEmail &&
            m.attachScheduledCalendarEvent &&
            !m.onScheduledCalendarEvent.allDay &&
            !control.value
          ) {
            return { required: true };
          }

          return null;
        }
      )
    );

    this.onScheduledCalendarForm.addControl(
      'sched_allDay',
      new UntypedFormControl(false, Validators.nullValidator)
    );
  }

  getAvailableRoles() {
    this._securitySvc
      .getRolesWithAnyActions(
        this._context.client ? this._context.client.id : null,
        Actions.RESPONSIBLE_ROLE_ACTIONS
      )
      .subscribe(roles => {
        this.availableScheduleRoles = roles;
        this.availableCompleteRoles = roles;
      });
  }

  // Override of the base method to update the main activity's column entities.
  // This is necessary to capture changes like display order when clicking SAVE
  // on the main activity editor, since save events that are raised for
  // individual steps are not propagated to the main activity editor.
  save(): boolean {
    let columnEntities: DataEntityLayoutModel[] = [];

    columnEntities.push(
      ...(this.requestStepActivity?.model
        .formLayoutModel as SingleColumnFormLayoutModel).columnEntities.filter(
        ce => ce.sectionId === ConsolidatedInspectionSections.REQUEST_SECTION_ID
      )
    );

    columnEntities.push(
      ...(this.scheduleStepActivity?.model
        .formLayoutModel as SingleColumnFormLayoutModel).columnEntities.filter(
        ce =>
          ce.sectionId === ConsolidatedInspectionSections.SCHEDULE_SECTION_ID
      )
    );

    columnEntities.push(
      ...(this.completeStepActivity?.model
        .formLayoutModel as SingleColumnFormLayoutModel).columnEntities.filter(
        ce =>
          ce.sectionId === ConsolidatedInspectionSections.COMPLETE_SECTION_ID
      )
    );

    (this.activity.model
      .formLayoutModel as SingleColumnFormLayoutModel).columnEntities = columnEntities;

    this.syncNoScheduleOnlyIndicesFromFlagged();

    this.resetModelSteps();

    this.cleanupOnScheduledCalendarConfig();

    return super.save();
  }

  /**
   * If OnScheduledEmail is turned off, wipe the calendar attachment state so
   * we don't persist orphaned configuration alongside a disabled email.
   */
  private cleanupOnScheduledCalendarConfig() {
    const model = this.activity.model as ConsolidatedInspectionActivityModel;

    if (!model.sendOnScheduledEmail) {
      model.attachScheduledCalendarEvent = false;
      model.onScheduledCalendarEvent = new ScheduledCalendarEventConfig();
    }
  }

  requestStepSaved(requestStepActivity: FormActivity<FormActivityModel>) {
    if (requestStepActivity.id !== this.requestStepActivity.id) {
      this.saved.emit(requestStepActivity as any);
      return;
    }

    let columnEntities: DataEntityLayoutModel[] = [];

    let requestActivityColumnEntities = (requestStepActivity.model
      .formLayoutModel as SingleColumnFormLayoutModel).columnEntities;

    for (let columnEntity of requestActivityColumnEntities) {
      columnEntity.sectionId =
        ConsolidatedInspectionSections.REQUEST_SECTION_ID;
    }

    columnEntities.push(...requestActivityColumnEntities);

    columnEntities.push(
      ...(this.activity.model
        .formLayoutModel as SingleColumnFormLayoutModel).columnEntities.filter(
        ce =>
          ce.sectionId === ConsolidatedInspectionSections.SCHEDULE_SECTION_ID
      )
    );

    columnEntities.push(
      ...(this.activity.model
        .formLayoutModel as SingleColumnFormLayoutModel).columnEntities.filter(
        ce =>
          ce.sectionId === ConsolidatedInspectionSections.COMPLETE_SECTION_ID
      )
    );

    (this.activity.model
      .formLayoutModel as SingleColumnFormLayoutModel).columnEntities = columnEntities;

    this.syncNoScheduleOnlyIndicesFromFlagged();

    this.resetModelSteps();

    this.saved.emit(this.activity);
  }

  scheduleStepSaved(scheduleStepActivity: FormActivity<FormActivityModel>) {
    if (scheduleStepActivity.id !== this.scheduleStepActivity.id) {
      this.saved.emit(scheduleStepActivity as any);
      return;
    }

    let columnEntities: DataEntityLayoutModel[] = [];

    let scheduleActivityColumnEntities = (scheduleStepActivity.model
      .formLayoutModel as SingleColumnFormLayoutModel).columnEntities;

    for (let columnEntity of scheduleActivityColumnEntities) {
      columnEntity.sectionId =
        ConsolidatedInspectionSections.SCHEDULE_SECTION_ID;
    }

    columnEntities.push(
      ...(this.activity.model
        .formLayoutModel as SingleColumnFormLayoutModel).columnEntities.filter(
        ce => ce.sectionId === ConsolidatedInspectionSections.REQUEST_SECTION_ID
      )
    );

    columnEntities.push(...scheduleActivityColumnEntities);

    columnEntities.push(
      ...(this.activity.model
        .formLayoutModel as SingleColumnFormLayoutModel).columnEntities.filter(
        ce =>
          ce.sectionId === ConsolidatedInspectionSections.COMPLETE_SECTION_ID
      )
    );

    (this.activity.model
      .formLayoutModel as SingleColumnFormLayoutModel).columnEntities = columnEntities;

    this.syncNoScheduleOnlyIndicesFromFlagged();

    this.resetModelSteps();

    this.saved.emit(this.activity);
  }

  completeStepSaved(completeStepActivity: FormActivity<FormActivityModel>) {
    if (completeStepActivity.id !== this.completeStepActivity.id) {
      this.saved.emit(completeStepActivity as any);
      return;
    }

    let columnEntities: DataEntityLayoutModel[] = [];

    let completeActivityColumnEntities = (completeStepActivity.model
      .formLayoutModel as SingleColumnFormLayoutModel).columnEntities;

    for (let columnEntity of completeActivityColumnEntities) {
      columnEntity.sectionId =
        ConsolidatedInspectionSections.COMPLETE_SECTION_ID;
    }

    columnEntities.push(
      ...(this.activity.model
        .formLayoutModel as SingleColumnFormLayoutModel).columnEntities.filter(
        ce => ce.sectionId === ConsolidatedInspectionSections.REQUEST_SECTION_ID
      )
    );

    columnEntities.push(
      ...(this.activity.model
        .formLayoutModel as SingleColumnFormLayoutModel).columnEntities.filter(
        ce =>
          ce.sectionId === ConsolidatedInspectionSections.SCHEDULE_SECTION_ID
      )
    );

    columnEntities.push(...completeActivityColumnEntities);

    (this.activity.model
      .formLayoutModel as SingleColumnFormLayoutModel).columnEntities = columnEntities;

    this.syncNoScheduleOnlyIndicesFromFlagged();

    this.resetModelSteps();

    this.saved.emit(this.activity);
  }

  resetModelSteps() {
    (this.activity
      .model as ConsolidatedInspectionActivityModel).requestStepActivity = null;
    (this.activity
      .model as ConsolidatedInspectionActivityModel).scheduleStepActivity = null;
    (this.activity
      .model as ConsolidatedInspectionActivityModel).completeStepActivity = null;
  }

  // ---------------------------------------------------------------------------
  // "No Schedule Only" column-entity flagging (sys-admin only)
  // ---------------------------------------------------------------------------

  /**
   * True when the given Complete Step column entity is currently flagged as
   * "No Schedule Only".
   */
  isNoScheduleOnly(ce: DataEntityLayoutModel): boolean {
    return this.noScheduleFlagged.has(ce);
  }

  /**
   * Toggle the "No Schedule Only" flag on a Complete Step column entity.
   * Silently ignored if the entity contains a system data entity — those can
   * never be No Schedule Only (they carry inspection-app-required plumbing).
   */
  toggleNoScheduleOnly(ce: DataEntityLayoutModel) {
    if (!ce || this.columnEntityContainsSystemEntity(ce)) {
      return;
    }

    if (this.noScheduleFlagged.has(ce)) {
      this.noScheduleFlagged.delete(ce);
    } else {
      this.noScheduleFlagged.add(ce);
    }
  }

  /**
   * Recursively determines whether the given column entity contains any
   * DataEntity whose `isSystemEntity` is true. Uses each layout model's
   * `getDataEntities()` which already walks nested table/row/group layouts,
   * so we get free recursion without touching the shared layout classes.
   */
  columnEntityContainsSystemEntity(ce: DataEntityLayoutModel): boolean {
    if (!ce) {
      return false;
    }

    const entities = ce.getDataEntities ? ce.getDataEntities() : [];

    return (entities ?? []).some(de => de?.isSystemEntity === true);
  }

  /**
   * Walks the parent activity's merged `formLayoutModel.columnEntities`
   * (which must already have been rebuilt for this save/section-save) and
   * emits the positional indices of Complete Step entries whose object
   * references are in `noScheduleFlagged`. This is the array that gets
   * persisted as `noScheduleOnlyColumnEntityIndices`.
   */
  private syncNoScheduleOnlyIndicesFromFlagged() {
    const model = this.activity.model as ConsolidatedInspectionActivityModel;

    const merged =
      (this.activity.model.formLayoutModel as SingleColumnFormLayoutModel)
        ?.columnEntities ?? [];

    const indices: number[] = [];

    for (let i = 0; i < merged.length; i++) {
      const ce = merged[i];

      if (
        ce?.sectionId === ConsolidatedInspectionSections.COMPLETE_SECTION_ID &&
        this.noScheduleFlagged.has(ce)
      ) {
        indices.push(i);
      }
    }

    model.noScheduleOnlyColumnEntityIndices = indices;
  }
}
